import { useCallback, useEffect, useState } from 'react'
import { useAtom, useSetAtom } from 'jotai'

import { HttpCode, Millisecond, STORAGE_KEY } from '@/constants/common'
import { deleteUrlParams, getUrlParams } from '@/lib/utils'
import { useGlobalMethodProviderContext } from '@/providers/GlobalMethodProvider'
import { twitterAuthStateAtom } from '@/stores/services'

import useFetch, { Method } from './useFetch'
import { useToken } from './useLoginWallet'

export enum TwitterAuthState {
  UN_KNOWN = 'UN_KNOWN',
  REDIRECTING = 'REDIRECTING',
  AUTHENTICATED = 'AUTHENTICATED',
  UNAUTHENTICATED = 'UNAUTHENTICATED',
  EXPIRED = 'EXPIRED',
  OCCUPIED = 'OCCUPIED'
}

export const useCheckTwitterAuth = () => {
  const { token } = useToken()
  const [twitterAuthState, setTwitterAuthState] = useAtom(twitterAuthStateAtom)

  const [state, authCode] = [getUrlParams('state'), getUrlParams('code')]

  return useFetch<null>(
    token && !(state && authCode) ? '/task/check/interact' : null,
    Method.POST,
    undefined,
    {
      onSuccess: (_, code) => {
        if (code === HttpCode.TwitterAuthExpired) {
          setTwitterAuthState(TwitterAuthState.EXPIRED)
        } else if (code === 0) {
          setTwitterAuthState(TwitterAuthState.AUTHENTICATED)
        } else {
          setTwitterAuthState(TwitterAuthState.UNAUTHENTICATED)
        }
      }
    },
    undefined,
    {
      refreshInterval:
        twitterAuthState === TwitterAuthState.REDIRECTING
          ? Millisecond.SECOND_1
          : undefined
    }
  )
}

export const useAuthCallback = () => {
  const { token } = useToken()
  const setTwitterAuthState = useSetAtom(twitterAuthStateAtom)
  const { refreshUserInfo, openTwitterAuth } = useGlobalMethodProviderContext()
  const [state, code] = [getUrlParams('state'), getUrlParams('code')]

  const { data, ...rest } = useFetch<any>(
    token && state && code ? '/task/x/auth/callback' : null,
    Method.POST,
    { state, code, domainType: 2 },
    {
      onSuccess: (_, code) => {
        if (code === 0) {
          setTwitterAuthState(TwitterAuthState.AUTHENTICATED)
          deleteUrlParams(['state', 'code'])
          refreshUserInfo()
        } else if (code === HttpCode.TwitterOccupied) {
          setTwitterAuthState(TwitterAuthState.OCCUPIED)
        } else {
          setTwitterAuthState(TwitterAuthState.EXPIRED)
        }
      }
    }
  )

  useEffect(() => {
    if (state && code) openTwitterAuth?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    state,
    code,
    data,
    ...rest
  }
}

export const useGetAuthLink = () => {
  const { token } = useToken()
  const [canFetch, setCanFetch] = useState(false)
  const [, setTwitterAuthState] = useAtom(twitterAuthStateAtom)

  const { mutate } = useFetch<{ authUrl: string }>(
    token && canFetch ? '/task/x/auth/link' : null,
    Method.POST,
    { domainType: 2 },
    {
      onSuccess: (data) => {
        if (!data.authUrl) return
        setTwitterAuthState(TwitterAuthState.REDIRECTING)
        window.localStorage.setItem(
          STORAGE_KEY.BEFORE_AUTH_ROUTE,
          location.pathname
        )
        location.href = data.authUrl
        // window.open(
        //   data.authUrl,
        //   '_self',
        //   'width=500,height=800,toolbar=no,menubar=no,scrollbars=yes,resizable=yes'
        // )
      }
    }
  )

  const getAuthLink = useCallback(() => {
    if (!canFetch) setCanFetch(true)
    else {
      mutate()
    }
  }, [canFetch, mutate])

  return { getAuthLink }
}

export const useTwitterAuth = () => {
  const { getAuthLink } = useGetAuthLink()
  const { state, code, isLoading, isValidating } = useAuthCallback()

  return { state, code, checkLoading: isLoading || isValidating, getAuthLink }
}
